import { makeObservable, observable, action, computed } from 'mobx';
import { getFirebase } from '@assets/firebase/FirebaseAdapater';
import { DataAccessUtils } from './DataAccessUtils';
import { useStore } from './Store';
import { FieldsSchema, SchemaFieldKeys } from './FieldsSchema';

// Helper function to check if a field is yearly
function isYearlyField(fieldKey: SchemaFieldKeys): boolean {
    // Type guard to ensure the field has the yearly property
    return 'yearly' in FieldsSchema[fieldKey] && FieldsSchema[fieldKey]['yearly'] === true;
}

interface ReferenceData {
    data: Record<string, any>;
    metadata: {
        saved_at: any;
        saved_by: string;
        saved_by_email: string;
        llm_version: string;
        last_modified: any;
    };
}

export class SelectionStore {
    @observable accessor isSelectionModeActive: boolean = false;
    @observable accessor selectedFields: Record<string, boolean> = {};
    @observable accessor existingDataFound: boolean = false;
    @observable accessor existingReferenceFields: Record<string, any> = {};
    
    constructor() {
        makeObservable(this);
    }
    
    @computed get selectionCount() {
        return Object.values(this.selectedFields).filter(Boolean).length;
    }
    
    @action
    toggleSelection(fieldName: string) {
        if (!this.isSelectionModeActive || !fieldName) {
            console.warn(`Toggle selection failed: isSelectionModeActive=${this.isSelectionModeActive}, fieldName=${fieldName}`);
            return;
        }
        
        console.log(`Toggle selection for ${fieldName}: current=${this.selectedFields[fieldName]}`);
        
        // Create new object to guarantee that MobX detects the change
        const newSelectedFields = { ...this.selectedFields };
        
        // Toggle the field in the new object
        newSelectedFields[fieldName] = !newSelectedFields[fieldName];
        
        // Replace the entire object instead of modifying in place
        this.selectedFields = newSelectedFields;
        
        console.log(`After toggle - field ${fieldName}: new value=${this.selectedFields[fieldName]}`);
        console.log(`Total selected fields: ${this.selectionCount}`);
    }
    
    @action
    async activateSelectionMode() {
        const store = useStore();
        const dealId = store.selectedReportId;
        
        if (!dealId) return;
        
        // Clear previous selections
        this.selectedFields = {};
        
        try {
            // Use get() instead of onValue for one-time data retrieval
            const existingData = await getFirebase().get_existing_reference_data(dealId) as ReferenceData;
            
            if (existingData && existingData.data) {
                // Mark that we found existing data
                this.existingDataFound = true;
                
                // existingData.data is already an object in format {fieldName: true}
                this.existingReferenceFields = existingData.data;
                
                // Use the existing fields object directly
                this.selectedFields = { ...existingData.data };
                
                console.log('Pre-selected fields:', this.selectedFields);
            } else {
                this.existingDataFound = false;
                this.existingReferenceFields = {};
            }
            
            // Activate selection mode
            this.isSelectionModeActive = true;
        } catch (error) {
            console.error('Error loading existing reference data:', error);
        }
    }
    
    @action
    selectAll() {
        const store = useStore();
        const dealId = store.selectedReportId;
        
        if (!dealId || !store.reportDataDB[dealId]) {
            console.warn('No active deal data available');
            return;
        }
        
        // Get all available fields from the current deal data
        const currentDealData = store.reportDataDB[dealId];
        const overrides = store.reportDataOverridesDB[dealId] || {};
        
        // Create DataAccessUtils to access the deal data
        const dataAccess = new DataAccessUtils(currentDealData, overrides, dealId);
        
        // Get all fields from FieldsSchema that have data in the current deal
        const allSchemaFields = Object.keys(FieldsSchema) as SchemaFieldKeys[];
        const availableFields = new Set<string>();
        
        // Use DataAccessUtils to check for available fields
        allSchemaFields.forEach(fieldKey => {
            // Use DataAccessUtils.value() method to properly access and validate fields
            const fieldValue = dataAccess.value(fieldKey);
            
            // Only add fields that have valid data (not 'N/A')
            if (fieldValue !== 'N/A' && fieldValue !== undefined && fieldValue !== null) {
                availableFields.add(fieldKey);
            }
            
            // For yearly data fields, check if they have data using valueByYear
            if (isYearlyField(fieldKey)) {
                try {
                    const currentYear = dataAccess.actualYear();
                    const yearlyValue = dataAccess.valueByYear(fieldKey, currentYear);
                    
                    if (yearlyValue !== 'N/A' && yearlyValue !== undefined && yearlyValue !== null) {
                        availableFields.add(fieldKey);
                    }
                } catch (e) {
                    // Field might not support valueByYear, skip it
                }
            }
        });
        
        console.log(`Found ${availableFields.size} available fields to select`);
        
        // Create an object with all fields set to true
        const allSelected = { ...this.selectedFields };
        
        // Add all available fields
        availableFields.forEach(fieldName => {
            allSelected[fieldName] = true;
        });
        
        this.selectedFields = allSelected;
    }
    
    @action
    async saveSelected() {
        const store = useStore();
        const dealId = store.selectedReportId;
        
        if (!dealId) {
            console.error('No dealId available for saving reference data');
            return;
        }
        
        try {
            // Filter to get only selected fields
            const fieldsToSave = { ...this.selectedFields };
            
            if (this.selectionCount === 0) {
                console.warn('No fields selected for saving');
                return;
            }
            
            // Save to Firebase
            await getFirebase().save_reference_data(dealId, fieldsToSave);
            
            // Exit selection mode
            this.exitSelectionMode();
        } catch (error) {
            console.error('Error in saveSelected:', error);
        }
    }
    
    @action
    exitSelectionMode() {
        this.isSelectionModeActive = false;
        this.selectedFields = {};
        this.existingDataFound = false;
        this.existingReferenceFields = {};
    }
    
    @action
    isFieldSelected(fieldName: string) {
        return this.selectedFields[fieldName] === true;
    }
}

// Create a singleton instance
const selectionStore = new SelectionStore();

// Hook to use the selection store
export const useSelectionStore = () => selectionStore; 